<template>
  <div class="earnings-report-card">
    <div class="earnings-header">
      <div class="header-left">
        <div class="earnings-icon">
          <i class="fas fa-chart-bar"></i>
        </div>
        <div class="header-text">
          <h2 class="earnings-title">Earnings Report</h2>
          <p class="earnings-description">Earnings for the year</p>
        </div>
      </div>
      <div class="total-earnings">${{ totalSales }}</div>
    </div>
    
    <div class="chart-container">
      <BarChart v-if="shouldRenderChart" :chart-data="formattedChartData" :options="chartOptions" />
      <div v-if="chartError" class="chart-error">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ chartError }}</p>
      </div>
      <div v-if="!shouldRenderChart && !chartError" class="chart-loading">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Loading chart data...</p>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, watch, nextTick } from 'vue';
import { BarChart } from 'vue-chart-3';
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default defineComponent({
  name: 'EarningReports',
  components: {
    BarChart
  },
  props: {
    totalSales: String,
    chartData: {
      type: Object,
      required: true
    },
  },
  setup(props) {
    const shouldRenderChart = ref(false);
    const chartError = ref(null);
    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 800,
        easing: 'easeOutQuart'
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 2000,
            callback: function(value) {
              return '$' + value;
            },
            font: {
              size: 11,
              family: "'Arial', sans-serif"
            },
            color: '#666'
          },
          grid: {
            display: true,
            drawBorder: false,
            color: function(context) {
              if (context.tick.value === 0) {
                return 'rgba(0, 0, 0, 0.1)';
              }
              return 'rgba(0, 0, 0, 0.05)';
            }
          }
        },
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            font: {
              size: 11,
              family: "'Arial', sans-serif"
            },
            color: '#666'
          }
        }
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(76, 107, 48, 0.8)',
          titleFont: {
            size: 13,
            family: "'Arial', sans-serif"
          },
          bodyFont: {
            size: 13,
            family: "'Arial', sans-serif"
          },
          padding: 12,
          cornerRadius: 4,
          displayColors: false,
          callbacks: {
            label: function(context) {
              return '$' + context.parsed.y.toFixed(2);
            }
          }
        }
      }
    };

    const formattedChartData = ref(null);

    watch(() => props.chartData, async (newChartData) => {
      if (newChartData && newChartData.datasets && Array.isArray(newChartData.datasets)) {
        // Create a gradient for the bars
        const getGradient = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const gradient = ctx.createLinearGradient(0, 0, 0, 400);
            gradient.addColorStop(0, '#4C6B30');
            gradient.addColorStop(1, '#6E9F49');
            return gradient;
          }
          return '#4C6B30';
        };

        formattedChartData.value = {
          ...newChartData,
          datasets: newChartData.datasets.map(dataset => ({
            ...dataset,
            backgroundColor: getGradient(),
            hoverBackgroundColor: '#6E9F49',
            borderColor: '#4C6B30',
            borderWidth: 1,
            borderRadius: 8,
            barPercentage: 0.6,
            categoryPercentage: 0.8
          }))
        };
        await nextTick();
        shouldRenderChart.value = true;
      } else {
        chartError.value = 'Invalid chart data structure. Please check the data format.';
      }
    }, { immediate: true });

    return {
      shouldRenderChart,
      chartError,
      chartOptions,
      formattedChartData
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.shouldRenderChart = true;
    });
  },
  errorCaptured(err, vm, info) {
    console.error('Error in component:', err, info);
    this.chartError = 'An error occurred while rendering the chart. Please check console for details.';
    return false;
  }
});
</script>

<style scoped>
.earnings-report-card {
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  margin-top: 1.5rem;
}

.earnings-report-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
}

.earnings-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
}

.header-left {
  display: flex;
  align-items: center;
}

.earnings-icon {
  background-color: #4C6B30;
  color: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-right: 1.25rem;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.earnings-title {
  margin: 0;
  font-size: 1.1rem;
  color: #333;
  font-weight: 600;
}

.earnings-description {
  margin: 0.25rem 0 0;
  color: #666;
  font-size: 0.9rem;
}

.total-earnings {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
}

.chart-container {
  padding: 1rem 1.5rem 1.5rem;
  height: 400px;
  position: relative;
}

.chart-error, .chart-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
}

.chart-error i, .chart-loading i {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #4C6B30;
}

.chart-error {
  color: #d32f2f;
}

.chart-loading {
  color: #4C6B30;
}

@media (max-width: 1024px) {
  .chart-container {
    height: 300px;
  }

  .earnings-icon {
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .earnings-title {
    font-size: 1rem;
  }

  .total-earnings {
    font-size: 1.5rem;
  }
}

@media (max-width: 768px) {
  .earnings-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-left {
    margin-bottom: 1rem;
    width: 100%;
  }

  .earnings-icon {
    margin-right: 1rem;
  }

  .total-earnings {
    font-size: 1.8rem;
  }
}

@media (max-width: 480px) {
  .earnings-header {
    padding: 1rem;
  }
  
  .header-left {
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 100%;
  }
  
  .earnings-icon {
    margin-right: 0;
    margin-bottom: 0.8rem;
  }
  
  .header-text {
    align-items: center;
  }
  
  .total-earnings {
    margin-top: 1rem;
    text-align: center;
    width: 100%;
  }
  
  .chart-container {
    padding: 0.8rem;
    height: 280px;
  }
}
</style>
